import { createTheme, ThemeProvider } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import { ptBR } from "@mui/material/locale";
import * as Sentry from "@sentry/react";
import * as React from "react";
import { hotjar } from "react-hotjar";
import { Provider } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import store from "./store/store";
import "./App.css";
import Router from "./routes";

function App() {
  const enviroment = process.env.REACT_APP_ENV_NAME;
  let theme = createTheme(
    {
      palette: {
        primary: {
          main: "#503DFC",
          ultraLight: "#F1F0FF",
          light: "#7363FF",
          dark: "#3C2EBD",
        },
        secondary: {
          main: "#820AD1",
          ultraLight: "#FAF2FF",
          light: "#ECDAF8",
          dark: "#4E067D",
        },
        error: {
          main: "#E71D36",
          ultraLight: "#FCE6EC",
          light: "#EC3162",
          dark: "#8B1120",
        },
        warning: {
          main: "#FF8500",
          ultraLight: "#FDF2E0",
          light: "#EF9700",
          dark: "#FF711A",
        },
        info: {
          main: "#2196F3",
          ultraLight: "#E5F2E0",
          light: "#6EC6FF",
          dark: "#0069C0",
        },
        success: {
          main: "#1A5F02",
          light: "#269800",
          dark: "#144702",
        },
        grey: {
          100: "#E0E0E0",
        },
        gradient: {
          main: "linear-gradient(127deg, #7232FB 12.41%, #41B0F8 87.99%)",
          light:
            "linear-gradient(254deg, #41B0F8 2.62%, #626DFD 51.81%, #7232FB 99.99%)",
        },
        background: {
          default: "#FFF",
        },
        typography: {
          primary: "#00010C",
          secondary: "#69708A",
          whiteText: "#FFF",
          heading: "#23313F",
          disabled: "#A6A6A5",
        },
      },
      components: {
        MuiButton: {
          styleOverrides: {
            root: {
              textTransform: "none",
              borderRadius: 50,
              textUnderlineOffset: "10px",
              "&.Mui-disabled": {
                opacity: 1,
                color: "#BCBCBC",
                backgroundColor: "#E7E7E7",
                borderColor: "#E7E7E7",
              },
            },
          },
        },
        MuiTablePagination: {
          styleOverrides: {
            selectLabel: {
              marginBottom: "0px!important",
            },
            displayedRows: {
              marginBottom: "0px!important",
            },
          },
        },
      },
      typography: {
        fontFamily: [
          "Poppins",
          "-apple-system",
          "BlinkMacSystemFont",
          "Roboto",
          '"Helvetica Neue"',
          "Arial",
          "sans-serif",
          '"Apple Color Emoji"',
          '"Segoe UI Emoji"',
          '"Segoe UI Symbol"',
        ].join(","),
        h1: {
          fontSize: "32px",
          fontWeight: 600,
          lineHeight: "40px",
        },
        h2: {
          fontSize: "24px",
          fontWeight: 600,
          lineHeight: "32px",
        },
        h3: {
          fontSize: "20px",
          fontWeight: 400,
          lineHeight: "28px",
        },
        h4: {
          fontSize: "18px",
          fontWeight: 600,
          lineHeight: "26px",
        },
        h5: {
          fontSize: "18px",
          fontWeight: 500,
          lineHeight: "26px",
        },
        h6: {
          fontSize: "16px",
          fontWeight: 600,
          lineHeight: "24px",
        },
        body1: {
          fontSize: "16px",
          fontWeight: 400,
          lineHeight: "24px",
        },
        caption: {
          fontSize: "14px",
          fontWeight: 400,
          lineHeight: "20px",
        },
      },
    },
    ptBR
  );

  React.useEffect(() => {
    const hotJarId = process.env.REACT_APP_HOTJAR_SITE_ID;
    if (enviroment === "PROD" && hotJarId) {
      const hotJarVersion = 6;
      hotjar.initialize(Number(hotJarId), hotJarVersion);
    }
  }, [enviroment]);

  Sentry.init({
    dsn: "https://283a38ce56e00d8d6774f22eba95962a@o4505760593936384.ingest.sentry.io/4505760610910208",
    environment: enviroment,
    integrations: [
      new Sentry.BrowserTracing({
        // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
        tracePropagationTargets: ["localhost", "https:yourserver.io/api/"],
      }),
      new Sentry.Replay({}),
    ],
    // Performance Monitoring
    tracesSampleRate: 1.0, // Capture 100% of the transactions, reduce in production!
    // Session Replay
    replaysSessionSampleRate: 0, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });

  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <ToastContainer
          rtl={false}
          autoClose={2000}
          pauseOnHover
          position={toast.POSITION.TOP_CENTER}
          theme="colored"
          limit={3}
          pauseOnFocusLoss
          newestOnTop
        />
        <CssBaseline />
        <Router />
      </ThemeProvider>
    </Provider>
  );
}

export default App;
